<template>
  <div class="layout-wrapper">
    <!-- 左侧菜单 -->
    <div class="sidebar">
      <!-- LOGO区域 -->
      <div class="sidebar-logo">
        <img :src="logoImage" class="logo-icon-img" :alt="brandName">
        <span class="logo-text">{{ brandName }}</span>
      </div>

      <!-- 菜单列表 -->
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        :unique-opened="true"
        background-color="transparent"
        text-color="#fff"
        active-text-color="#fff"
        router
        class="sidebar-menu"
      >
        <!-- 首页仪表盘（固定 + 设置按钮） -->
        <div class="menu-item-with-setting">
          <el-menu-item
            v-if="homeMenu"
            :key="homeMenu.index"
            :index="homeMenu.index"
            :route="homeMenu.route"
            class="home-menu-item"
          >
            <i :class="homeMenu.icon"></i>
            <span slot="title">{{ homeMenu.title }}</span>
          </el-menu-item>
          <i class="el-icon-setting menu-setting-btn" @click="openMenuOrderDialog" title="自定义菜单"></i>
        </div>

        <!-- 其他菜单项 -->
        <template v-for="menu in otherMenus">
          <!-- 普通菜单项 -->
          <el-menu-item
            v-if="!menu.children && (menu.show !== 'showCommunityMenu' || showCommunityMenu)"
            :key="menu.index"
            :index="menu.index"
            :route="menu.route"
          >
            <i :class="menu.icon"></i>
            <span slot="title">{{ menu.title }}</span>
          </el-menu-item>

          <!-- 带子菜单的项 -->
          <el-submenu
            v-else-if="menu.children"
            :key="menu.index"
            :index="menu.index"
          >
            <template slot="title">
              <i :class="menu.icon"></i>
              <span>{{ menu.title }}</span>
            </template>

            <!-- 子菜单项 -->
            <el-menu-item
              v-for="child in menu.children"
              :key="child.index"
              :index="child.index"
              :route="child.route"
            >
              <i :class="child.icon"></i>
              <span slot="title">{{ child.title }}</span>
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>
    </div>

    <!-- 右侧内容区 -->
    <div class="main-container">
      <!-- 顶部导航栏 -->
      <div class="top-header">
        <div class="header-left">
          <el-button 
            type="text" 
            :icon="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"
            @click="toggleCollapse"
            class="collapse-btn"
          >
          </el-button>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>首页</el-breadcrumb-item>
            <el-breadcrumb-item>{{ currentPageTitle }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div class="header-right">
          <div class="header-item">
            <notification-bell />
          </div>
          <div class="header-item">
            <i class="el-icon-full-screen" @click="toggleFullScreen"></i>
          </div>
          <!-- 物业账号绑定显示：集团显示公司名称，物业公司显示小区名称 -->
          <div v-if="isPropertyAccount && headerDisplayName" class="community-badge">
            <i :class="accountType === 'GROUP' ? 'el-icon-s-custom' : 'el-icon-office-building'"></i>
            <span class="community-name">{{ headerDisplayName }}</span>
            <el-tag v-if="accountType === 'GROUP'" size="mini" type="warning" style="margin-left: 8px;">集团</el-tag>
            <el-tag v-else size="mini" type="success" style="margin-left: 8px;">物业</el-tag>
          </div>
          <!-- 城市运营商绑定城市显示 -->
          <div v-if="isOperatorRoute" class="city-badge">
            <i class="el-icon-location-outline"></i>
            <span class="city-name">{{ boundCity || '未绑定城市' }}</span>
          </div>
          <el-dropdown @command="handleCommand" trigger="click">
            <div class="user-info">
              <el-avatar icon="el-icon-user-solid" size="small"></el-avatar>
              <span class="username">{{ username }}</span>
              <i class="el-icon-arrow-down"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">
                <i class="el-icon-user"></i> 个人中心
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>

      <!-- 主内容区 -->
      <div class="main-content">
        <router-view></router-view>
      </div>
    </div>

    <!-- 菜单排序对话框 -->
    <el-dialog
      title="自定义菜单"
      :visible.sync="showMenuOrderDialog"
      width="550px"
      :close-on-click-modal="false"
    >
      <div class="menu-order-content">
        <p class="tip-text">勾选显示/隐藏菜单，点击 ↑↓ 调整顺序</p>
        <div v-for="(menu, index) in orderedMenus" :key="menu.index" class="menu-item-row">
          <el-checkbox v-model="menu.visible" :label="menu.title" @change="onVisibleChange(menu)">
            <i :class="menu.icon"></i>
            <span>{{ menu.title }}</span>
          </el-checkbox>
          <!-- 非首页仪表盘的菜单才显示箭头按钮 -->
          <div v-if="menu.index !== '/operator/dashboard' && menu.route !== '/operator/dashboard'" class="order-buttons">
            <el-button 
              size="mini" 
              circle 
              :disabled="index === 1" 
              @click="moveUp(index)"
              icon="el-icon-arrow-up"
              title="上移"
            ></el-button>
            <el-button 
              size="mini" 
              circle 
              :disabled="index === orderedMenus.length - 1" 
              @click="moveDown(index)"
              icon="el-icon-arrow-down"
              title="下移"
            ></el-button>
          </div>
          <!-- 首页仪表盘显示固定标识 -->
          <span v-else class="fixed-label">固定</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showMenuOrderDialog = false">取消</el-button>
        <el-button @click="resetMenuOrder" type="warning">重置默认</el-button>
        <el-button type="primary" @click="saveMenuOrder">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCurrentPropertyAccountCommunity } from '@/api/propertyAccount'
import { getCurrentCity } from '@/api/cityOperator'
import NotificationBell from './NotificationBell.vue'

console.log('MainLayout 开始加载...')

export default {
  name: 'MainLayout',
  components: {
    NotificationBell
  },
  data() {
    console.log('MainLayout data 初始化')
    return {
      isCollapse: false,
      showMenuOrderDialog: false,
      orderedMenus: [],
      username: localStorage.getItem('username') || '管理员',
      boundCommunity: null, // 物业账号绑定的小区名称
      boundCity: null, // 城市运营商绑定的城市名称
      accountType: localStorage.getItem('accountType') || '', // GROUP:集团, SINGLE:物业公司
      accountName: localStorage.getItem('accountName') || '', // 公司名称
      headerDisplayName: '' // 右上角显示的名称（集团显示公司名称，物业显示小区名）
    }
  },
  beforeCreate() {
    console.log('MainLayout beforeCreate')
  },
  created() {
    console.log('MainLayout created')
  },
  computed: {
    // 默认物业菜单 - 根据账号类型返回不同菜单
    defaultPropertyMenus() {
      console.log('defaultPropertyMenus computed, accountType:', this.accountType)
      
      const isGroup = this.accountType === 'GROUP'
      
      // 集团账户菜单（管理性质）
      const groupMenus = [
        { index: '/', route: '/', icon: 'el-icon-s-data', title: '首页仪表盘' },
        { index: '/community', route: '/community', icon: 'el-icon-office-building', title: '小区管理' },
        { index: '/property-company', route: '/property-company', icon: 'el-icon-s-shop', title: '物业公司' },
        { index: '/report', route: '/report', icon: 'el-icon-s-marketing', title: '数据报表' },
        { index: '/finance', route: '/finance', icon: 'el-icon-s-finance', title: '财务管理' },
        { index: '/system', route: '/system', icon: 'el-icon-s-tools', title: '系统设置' }
      ]
      
      // 物业公司菜单（业务操作）
      const propertyMenus = [
        { index: '/', route: '/', icon: 'el-icon-s-data', title: '首页仪表盘' },
        { index: '/community', route: '/community', icon: 'el-icon-office-building', title: '小区管理' },
        { index: '/merchant', route: '/merchant', icon: 'el-icon-shopping-cart-2', title: '商户管理' },
        { index: '/owner', route: '/owner', icon: 'el-icon-user', title: '业主管理' },
        { index: 'property-fee', route: null, icon: 'el-icon-money', title: '物业收费', children: [
          { index: '/property/cashier', route: '/property/cashier', icon: 'el-icon-s-shop', title: '收银台' },
          { index: '/property/smartCalc', route: '/property/smartCalc', icon: 'el-icon-s-promotion', title: '智能算费' },
          { index: '/property/bill', route: '/property/bill', icon: 'el-icon-s-order', title: '物业账单' },
          { index: '/property/statistics', route: '/property/statistics', icon: 'el-icon-s-data', title: '数据统计' },
          { index: '/property/prepaid', route: '/property/prepaid', icon: 'el-icon-wallet', title: '预存管理' },
          { index: '/property/settings', route: '/property/settings', icon: 'el-icon-s-tools', title: '收费设置' },
          { index: '/property/feeStandard', route: '/property/feeStandard', icon: 'el-icon-coin', title: '算费标准' },
          { index: '/property/parking-fee', route: '/property/parking-fee', icon: 'el-icon-s-finance', title: '车位管理费' },
          { index: '/property/meter-reading', route: '/property/meter-reading', icon: 'el-icon-odometer', title: '抄表录入' }
        ]},
        { index: 'merchant-fee', route: null, icon: 'el-icon-office-building', title: '商户收费', children: [
          { index: '/property/merchant-cashier', route: '/property/merchant-cashier', icon: 'el-icon-s-shop', title: '商户收银台' },
          { index: '/property/merchant-smart-calc', route: '/property/merchant-smart-calc', icon: 'el-icon-s-promotion', title: '商户算费' },
          { index: '/property/merchant-fee-standard', route: '/property/merchant-fee-standard', icon: 'el-icon-coin', title: '商户收费标准' }
        ]},
        { index: '/vehicle', route: '/vehicle', icon: 'el-icon-truck', title: '车辆管理' },
        { index: '/complaint', route: '/complaint', icon: 'el-icon-s-comment', title: '投诉管理' },
        { index: '/repair', route: '/repair', icon: 'el-icon-s-tools', title: '报修管理' },
        { index: '/patrol', route: '/patrol', icon: 'el-icon-s-check', title: '巡检管理' },
        { index: '/report', route: '/report', icon: 'el-icon-s-marketing', title: '报表统计' },
        { index: '/system', route: '/system', icon: 'el-icon-s-tools', title: '系统设置' }
      ]
      
      return isGroup ? groupMenus : propertyMenus
    },
    // 默认运营商菜单
    defaultOperatorMenus() {
      return [
        { index: '/operator/dashboard', route: '/operator/dashboard', icon: 'el-icon-s-data', title: '仪表盘' },
        { index: '/operator/community', route: '/operator/community', icon: 'el-icon-office-building', title: '小区管理' },
        { index: '/operator/property', route: '/operator/property', icon: 'el-icon-office-building', title: '物企管理' },
        { index: '/operator/team-leader', route: '/operator/team-leader', icon: 'el-icon-user', title: '团长管理' },
        { index: '/operator/owner', route: '/operator/owner', icon: 'el-icon-user', title: '业主管理' },
                {
          index: 'community-merchant',
          icon: 'el-icon-s-shop',
          title: '社商管理',
          children: [
            { index: '/operator/community-merchant', route: '/operator/community-merchant', icon: 'el-icon-s-shop', title: '社商列表' }
          ]
        },
        {
          index: 'saved-value-management',
          icon: 'el-icon-wallet',
          title: '储值卡管理',
          children: [
            { index: '/operator/community-merchant/prepaid', route: '/operator/community-merchant/prepaid', icon: 'el-icon-shop', title: '储值卡商户管理' },
            { index: '/operator/saved-value/merchant', route: '/operator/saved-value/merchant', icon: 'el-icon-user', title: '储值卡客户管理' },
            { index: '/operator/saved-value/refund', route: '/operator/saved-value/refund', icon: 'el-icon-document-checked', title: '退款审核' }
          ]
        },
        {
          index: 'community-member-card',
          icon: 'el-icon-s-ticket',
          title: '社商通管理',
          children: [
            { index: '/operator/community-merchant/member-card', route: '/operator/community-merchant/member-card', icon: 'el-icon-user', title: '用户管理' },
            { index: '/operator/community-merchant/cooperative-bank', route: '/operator/community-merchant/cooperative-bank', icon: 'el-icon-bank-card', title: '合作银行' }
          ]
        },
        { index: 'operator-supermarket', route: null, icon: 'el-icon-shopping-cart-full', title: '社区超市管理', children: [
          { index: '/operator/supermarket/merchant', route: '/operator/supermarket/merchant', icon: 'el-icon-s-shop', title: '商家审核' },
          { index: '/operator/supermarket/product', route: '/operator/supermarket/product', icon: 'el-icon-goods', title: '商品审核' },
          { index: '/operator/supermarket/share', route: '/operator/supermarket/share', icon: 'el-icon-pie-chart', title: '分成配置' },
          { index: '/operator/supermarket/order', route: '/operator/supermarket/order', icon: 'el-icon-s-order', title: '超市订单' },
          { index: '/operator/supermarket/settlement', route: '/operator/supermarket/settlement', icon: 'el-icon-document', title: '结算对账' }
        ]},
        {
          index: 'operator-secondhand',
          icon: 'el-icon-refresh',
          title: '二手市场管理',
          children: [
            { index: '/operator/secondhand', route: '/operator/secondhand', icon: 'el-icon-s-data', title: '数据概览' },
            { index: '/operator/secondhand/category', route: '/operator/secondhand/category', icon: 'el-icon-menu', title: '分类管理' },
            { index: '/operator/secondhand/item', route: '/operator/secondhand/item', icon: 'el-icon-goods', title: '物品管理' },
            { index: '/operator/secondhand/trade', route: '/operator/secondhand/trade', icon: 'el-icon-s-order', title: '交易管理' }
          ]
        },
        {
          index: 'delivery',
          icon: 'el-icon-truck',
          title: '蜂送到家',
          children: [
            { index: '/operator/delivery', route: '/operator/delivery', icon: 'el-icon-office-building', title: '驿站管理' },
            { index: '/operator/delivery/settings', route: '/operator/delivery/settings', icon: 'el-icon-setting', title: '功能设置' }
          ]
        },
        {
          index: 'water-station',
          icon: 'el-icon-s-platform',
          title: '水站管理',
          children: [
            { index: '/operator/water/order', route: '/operator/water/order', icon: 'el-icon-s-order', title: '订单管理' },
            { index: '/operator/water/factories', route: '/operator/water/factories', icon: 'el-icon-office-building', title: '水厂审核' },
            { index: '/operator/water/station-audit', route: '/operator/water/station-audit', icon: 'el-icon-s-check', title: '开通审核' },
            { index: '/operator/water/community-pricing', route: '/operator/water/community-pricing', icon: 'el-icon-s-finance', title: '分成设置' },

            { index: '/operator/water/settlements', route: '/operator/water/settlements', icon: 'el-icon-document', title: '结算管理' },
            { index: '/operator/water/supplier-audit', route: '/operator/water/supplier-audit', icon: 'el-icon-s-check', title: '供货商审核' }
          ]
        },
        {
          index: 'marketing-management',
          icon: 'el-icon-present',
          title: '营销管理',
          children: [
            { index: '/operator/seckill', route: '/operator/seckill', icon: 'el-icon-lightning', title: '秒杀管理' },
            { index: '/operator/group', route: '/operator/group', icon: 'el-icon-s-goods', title: '团购管理' }
          ]
        },
        {
          index: 'ad-management',
          icon: 'el-icon-picture-outline',
          title: '广告管理',
          children: [
            { index: '/operator/ad/dashboard', route: '/operator/ad/dashboard', icon: 'el-icon-s-data', title: '数据汇总' },
            { index: '/operator/ad/self', route: '/operator/ad/self', icon: 'el-icon-picture', title: '自用Banner' },
            { index: '/operator/ad/commercial', route: '/operator/ad/commercial', icon: 'el-icon-s-promotion', title: '商业Banner' },
            { index: '/operator/ad/coupon-center', route: '/operator/ad/coupon-center', icon: 'el-icon-s-ticket', title: '券中心' },
            { index: '/operator/ad/bid-ranking', route: '/operator/ad/bid-ranking', icon: 'el-icon-s-data', title: '竞价排名' },
            { index: '/operator/ad/owner-marquee', route: '/operator/ad/owner-marquee', icon: 'el-icon-s-flag', title: '跑马灯管理' },
            { index: '/operator/ad/coupon-issue', route: '/operator/ad/coupon-issue', icon: 'el-icon-s-ticket', title: '发券管理' }
          ]
        },
        {
          index: 'finance-management',
          icon: 'el-icon-s-finance',
          title: '财务管理',
          children: [
            { index: '/operator/finance/revenue-share', route: '/operator/finance/revenue-share', icon: 'el-icon-s-order', title: '分成管理' },
            { index: '/operator/finance/wallet', route: '/operator/finance/wallet', icon: 'el-icon-wallet', title: '钱包' }
          ]
        },
        { index: '/operator/settings', route: '/operator/settings', icon: 'el-icon-setting', title: '系统设置' }
      ]
    },
    // 当前使用的菜单（物业或运营商）
    currentMenus() {
      console.log('currentMenus computed, isOperatorRoute:', this.isOperatorRoute)
      const defaultMenus = this.isOperatorRoute ? this.defaultOperatorMenus : this.defaultPropertyMenus
      
      // 优先读取包含visible状态的settings
      const savedSettings = localStorage.getItem(this.isOperatorRoute ? 'operatorMenuSettings' : 'propertyMenuSettings')
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings)
          if (Array.isArray(parsed) && parsed.length > 0) {
            console.log('使用保存的菜单设置:', parsed)
            const visibleMap = {}
            const orderList = []
            parsed.forEach(m => {
              visibleMap[m.index] = m.visible !== false
              orderList.push(m.index)
            })
            
            const result = []
            orderList.forEach(index => {
              if (visibleMap[index]) {
                const defaultMenu = defaultMenus.find(m => m.index === index)
                if (defaultMenu) {
                  result.push(defaultMenu)
                }
              }
            })
            
            defaultMenus.forEach(menu => {
              if (!orderList.includes(menu.index)) {
                result.push(menu)
              }
            })
            return result
          }
        } catch (e) {
          console.error('解析菜单设置失败:', e)
        }
      }
      
      // 兼容旧版
      const savedOrder = localStorage.getItem(this.isOperatorRoute ? 'operatorMenuOrder' : 'propertyMenuOrder')
      if (savedOrder) {
        try {
          const parsed = JSON.parse(savedOrder)
          if (Array.isArray(parsed) && parsed.length > 0) {
            console.log('使用保存的菜单顺序(旧版):', parsed)
            const savedIndexes = parsed.map(m => m.index)
            const result = []
            parsed.forEach(saved => {
              const defaultMenu = defaultMenus.find(m => m.index === saved.index)
              if (defaultMenu) {
                result.push(defaultMenu)
              }
            })
            defaultMenus.forEach(menu => {
              if (!savedIndexes.includes(menu.index)) {
                result.push(menu)
              }
            })
            return result
          }
        } catch (e) {
          console.error('解析菜单顺序失败:', e)
        }
      }
      
      console.log('使用默认菜单')
      return defaultMenus
    },
    // 首页仪表盘（固定在第一位）
    homeMenu() {
      const menus = this.currentMenus
      // 运营商首页就是 /operator/dashboard
      return menus.find(m => m.index === '/operator/dashboard' || m.route === '/operator/dashboard')
    },
    // 其他菜单项（除了首页仪表盘）
    otherMenus() {
      const menus = this.currentMenus
      return menus.filter(m => m.index !== '/operator/dashboard' && m.route !== '/operator/dashboard')
    },
    activeMenu() {
      return this.$route.path
    },
    currentPageTitle() {
      // 根据路由返回页面标题
      const titles = {
        '/': '首页仪表盘',
        '/owner': '业主管理',
        '/community': '小区管理',
        '/merchant': '商户管理',
        '/repair': '报修管理',
        '/complaint': '投诉管理',
        '/patrol': '巡检管理',
        '/vehicle': '车辆管理',
        '/property/cashier': '收银台',
        '/property/smartCalc': '智能算费',
        '/property/statistics': '数据统计',
        '/property/prepaid': '预存管理',
        '/property/settings': '收费设置',
        '/property/feeStandard': '算费标准',
        '/property/meter-reading': '抄表录入',
        '/property/bill': '物业账单',
        '/property/parking-fee': '车位管理费',
        '/property/merchant-cashier': '商户收银台',
        '/property/merchant-smart-calc': '商户智能算费',
        '/property/merchant-fee-standard': '商户收费标准',
        '/report': '报表统计',
        '/system': '系统设置',
        '/operator/dashboard': '仪表盘',
        '/operator/user': '用户管理',
        '/operator/property': '物企管理',
        '/operator/team-leader': '团长管理',
        '/operator/community-merchant': '社商管理',
        '/operator/product': '商品管理',
        '/operator/stock-record': '库存记录',
        '/operator/purchase-order': '采购订单',
        '/operator/delivery': '驿站管理',
        '/operator/delivery/settings': '功能设置',
        '/operator/settings': '系统设置',
        '/operator/ad/self': '自用广告',
        '/operator/ad/commercial': '商业广告',
        '/operator/finance/revenue-share': '分成管理',
        '/operator/finance/wallet': '钱包',
        '/operator/community-merchant': '社商列表',
        '/operator/community-merchant/member-card': '用户管理',
        '/operator/community-merchant/cooperative-bank': '合作银行',
        '/operator/community-merchant/prepaid': '储值卡商户管理',
        '/operator/saved-value/merchant': '储值卡客户管理',
        '/operator/saved-value/refund': '退款审核',
        '/operator/community-merchant/promotion': '推广管理',
        '/operator/community-merchant/coupon-center': '券中心',
        '/operator/community-merchant/bid-ranking': '竞价排名',
        '/operator/community-merchant/owner-ad': '业主端广告位',
        '/operator/community-merchant/property-ad': '物业端广告位',
        '/operator/community-merchant/ad-share': '广告分成设置',
        '/operator/seckill': '秒杀管理',
        '/operator/seckill/create': '创建秒杀',
        '/operator/group': '团购管理',
        '/operator/group/create': '创建团购',
        '/operator/water/order': '订单管理',
        '/operator/water/factories': '水厂审核',
        '/operator/water/station-audit': '开通审核',
        '/operator/water/community-pricing': '分成设置',
        '/operator/water/settlements': '结算管理',
        '/operator/water/supplier-audit': '供货商审核'
      }
      return titles[this.$route.path] || '首页'
    },
    // 判断是否是城市运营商路由
    isOperatorRoute() {
      const routePath = this.$route.path
      const result = routePath.startsWith('/operator') || routePath.startsWith('/city-operator')
      console.log('isOperatorRoute check:', routePath, '->', result)
      return result
    },
    // 判断是否是物业账号（非城市运营商且非admin账号）
    // 注释：临时允许admin账号也显示小区名称用于测试
    isPropertyAccount() {
      const username = localStorage.getItem('username')
      // 临时修改：允许所有非城市运营商账号显示小区名称
      return username && !this.isOperatorRoute
    },
    // 判断是否显示小区管理菜单（仅集团用户显示）
    showCommunityMenu() {
      // 如果是城市运营商，不显示
      if (this.isOperatorRoute) return false

      // 如果是admin账号，显示（用于测试）
      const username = localStorage.getItem('username')
      if (username === 'admin') return true

      // 如果是物业账号，需要获取账号类型（GROUP:集团用户, SINGLE:普通物业公司）
      const accountType = localStorage.getItem('accountType')
      return accountType === 'GROUP' || accountType === 'group'
    },
    // 根据路由显示不同品牌名称
    brandName() {
      return this.isOperatorRoute ? '蜂巢e社区-CY' : '蜜蜂公社AI物业管理系统'
    },
    // 根据路由显示不同logo
    logoImage() {
      return this.isOperatorRoute ? '/img/hive-logo-horizontal.png' : '/logo.png'
    }
  },
  mounted() {
    const routePath = this.$route.path
    console.log('MainLayout mounted, route:', routePath)
    console.log('isOperatorRoute:', this.isOperatorRoute)
    console.log('isPropertyAccount:', this.isPropertyAccount)
    console.log('localStorage username:', localStorage.getItem('username'))
    console.log('localStorage accountType:', localStorage.getItem('accountType'))
    // 如果是物业账号，获取绑定的小区信息
    if (this.isPropertyAccount) {
      console.log('调用 loadBoundCommunity()')
      this.loadBoundCommunity()
    }
    // 如果是城市运营商，获取绑定的城市信息
    if (this.isOperatorRoute) {
      console.log('调用 loadBoundCity()')
      this.loadBoundCity()
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapse = !this.isCollapse
    },
    // 菜单排序相关方法
    openMenuOrderDialog() {
      // 从localStorage读取保存的菜单设置
      const storageKey = this.isOperatorRoute ? 'operatorMenuSettings' : 'propertyMenuSettings'
      const savedSettings = localStorage.getItem(storageKey)
      let savedVisibleMap = {}
      let savedOrderList = []
      
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings)
          // 构建可见性映射和顺序列表
          parsed.forEach(m => {
            savedVisibleMap[m.index] = m.visible
          })
          savedOrderList = parsed.map(m => m.index)
        } catch (e) {
          console.error('解析菜单设置失败:', e)
        }
      }
      
      // 获取默认菜单（包含带children的父菜单，排除首页）
      const defaultMenus = (this.isOperatorRoute ? this.defaultOperatorMenus : this.defaultPropertyMenus)
        .filter(m => m.route !== '/operator/dashboard' && m.index !== '/operator/dashboard')
      
      // 如果有保存的顺序，按保存的顺序排列；否则使用默认顺序
      let orderedList = []
      if (savedOrderList.length > 0) {
        // 先添加保存顺序中的菜单
        savedOrderList.forEach(index => {
          const menu = defaultMenus.find(m => m.index === index)
          if (menu) {
            orderedList.push({
              ...menu,
              visible: savedVisibleMap[index] !== false // 默认为true
            })
          }
        })
        // 再添加保存顺序中没有的新菜单（默认显示）
        defaultMenus.forEach(menu => {
          if (!savedOrderList.includes(menu.index)) {
            orderedList.push({ ...menu, visible: true })
          }
        })
      } else {
        // 没有保存设置，使用默认顺序，全部显示
        orderedList = defaultMenus.map(m => ({ ...m, visible: true }))
      }
      
      this.orderedMenus = orderedList
      this.showMenuOrderDialog = true
    },
    moveUp(index) {
      if (index > 0) {
        const temp = this.orderedMenus[index]
        this.$set(this.orderedMenus, index, this.orderedMenus[index - 1])
        this.$set(this.orderedMenus, index - 1, temp)
      }
    },
    moveDown(index) {
      if (index < this.orderedMenus.length - 1) {
        const temp = this.orderedMenus[index]
        this.$set(this.orderedMenus, index, this.orderedMenus[index + 1])
        this.$set(this.orderedMenus, index + 1, temp)
      }
    },
    onVisibleChange(menu) {
      // 勾选变化时的回调（可选）
    },
    saveMenuOrder() {
      const storageKey = this.isOperatorRoute ? 'operatorMenuSettings' : 'propertyMenuSettings'
      const orderStorageKey = this.isOperatorRoute ? 'operatorMenuOrder' : 'propertyMenuOrder'
      // 保存所有菜单的设置（包括visible状态和顺序）
      const savedMenus = this.orderedMenus.map(m => ({
        index: m.index,
        route: m.route,
        icon: m.icon,
        title: m.title,
        visible: m.visible !== false // 确保有visible属性
      }))
      // 同时保存到新的settings key和旧的order key（兼容现有逻辑）
      localStorage.setItem(storageKey, JSON.stringify(savedMenus))
      // 只保存可见的菜单到旧key，供currentMenus使用
      const visibleMenus = savedMenus.filter(m => m.visible).map(m => ({
        index: m.index,
        route: m.route,
        icon: m.icon,
        title: m.title
      }))
      localStorage.setItem(orderStorageKey, JSON.stringify(visibleMenus))
      this.showMenuOrderDialog = false
      this.$message({
        message: '菜单设置已保存',
        type: 'success',
        duration: 2000
      })
      location.reload()
    },
    resetMenuOrder() {
      const orderStorageKey = this.isOperatorRoute ? 'operatorMenuOrder' : 'propertyMenuOrder'
      const settingsStorageKey = this.isOperatorRoute ? 'operatorMenuSettings' : 'propertyMenuSettings'
      localStorage.removeItem(orderStorageKey)
      localStorage.removeItem(settingsStorageKey)
      this.orderedMenus = (
        this.isOperatorRoute ? this.defaultOperatorMenus : this.defaultPropertyMenus
      ).filter(m => m.route !== '/operator/dashboard' && m.index !== '/operator/dashboard').map(m => ({...m, visible: true}))
      this.$message({
        message: '已重置为默认设置',
        type: 'success',
        duration: 1500
      })
    },
    toggleFullScreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen()
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        }
      }
    },
    // 加载物业账号绑定的小区信息
    async loadBoundCommunity() {
      try {
        // 从localStorage获取账号类型和名称
        this.accountType = localStorage.getItem('accountType') || ''
        this.accountName = localStorage.getItem('accountName') || ''

        // 临时测试：admin账号也显示模拟数据
        const username = localStorage.getItem('username')
        if (username === 'admin') {
          // 为admin账号设置一个临时的模拟数据
          this.accountType = 'SINGLE'
          this.accountName = '测试物业公司'
          this.boundCommunity = '幸福家园（测试）'
          this.headerDisplayName = this.boundCommunity
          return
        }

        const res = await getCurrentPropertyAccountCommunity()
        if (res.data) {
          this.boundCommunity = res.data.communityName || ''
          
          // 根据账号类型决定右上角显示什么
          if (this.accountType === 'GROUP') {
            // 集团账户显示公司名称
            this.headerDisplayName = this.accountName || '集团账户'
          } else {
            // 物业公司账户显示小区名称
            this.headerDisplayName = this.boundCommunity || '未绑定小区'
          }
        }
      } catch (error) {
        console.error('获取绑定小区信息失败:', error)
        // 失败时不显示，静默处理
      }
    },
    // 加载城市运营商绑定的城市信息
    async loadBoundCity() {
      try {
        const username = localStorage.getItem('username')
        // 优先用登录时保存的cityName做初始显示
        const savedCity = localStorage.getItem('cityName')
        if (savedCity) {
          this.boundCity = savedCity
        }
        // 如果 username 为空或为 admin，不调用接口
        if (!username || username === 'admin') {
          if (!savedCity) {
            this.boundCity = username === 'admin' ? '北京市（测试）' : '未绑定城市'
          }
          return
        }

        const res = await getCurrentCity(username)
        if (res && res.data && res.data.city) {
          this.boundCity = res.data.city
          // 同步更新localStorage
          localStorage.setItem('cityName', res.data.city)
        } else if (!this.boundCity) {
          this.boundCity = '未绑定城市'
        }
      } catch (error) {
        console.error('获取绑定城市信息失败:', error)
        if (!this.boundCity) {
          this.boundCity = '未绑定城市'
        }
      }
    },
    handleCommand(command) {
      switch(command) {
        case 'profile':
          this.$message.info('个人中心功能开发中')
          break
        case 'settings':
          this.$message.info('账号设置功能开发中')
          break
        case 'logout':
          this.$confirm('确定要退出登录吗?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            localStorage.removeItem('isLogin')
            localStorage.removeItem('username')
            this.$message.success('已退出登录')
            this.$router.push('/login')
          }).catch(() => {})
          break
        /*
        case 'resetMenu':
          this.resetMenuOrder()
          break
        */
      }
    }
  }
}
</script>

<style scoped>
.layout-wrapper {
  display: flex;
  width: 100%;
  height: 100vh;
  background: #fff;
  overflow: hidden;
}

/* 侧边栏 - 使用 CSS 变量自动切换主题 */
.sidebar {
  width: 220px;
  transition: width 0.2s;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

/* 拖拽模式开关 */
.drag-mode-switch {
  padding: 10px 15px;
  background: rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-logo {
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 15px;
  gap: 8px;
  flex-shrink: 0;
}

.logo-icon-img {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.logo-text {
  font-size: 15px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-menu {
  border-right: none;
  flex: 1;
  overflow-y: auto;
}

/* 滚动条样式 */
.sidebar-menu::-webkit-scrollbar {
  width: 6px;
}

.sidebar-menu::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-menu::-webkit-scrollbar-track {
  background: transparent;
}

/* 首页菜单项带设置按钮 - 使用 CSS 变量 */
.menu-item-with-setting {
  position: relative;
  display: flex;
  align-items: center;
  height: 50px;
  margin: 4px 0;
  overflow: visible !important;
}

.menu-item-with-setting .home-menu-item {
  background: transparent !important;
  flex: 1;
  overflow: visible !important;
  height: 50px;
  line-height: 50px;
  margin: 0;
}

.menu-setting-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #ffffff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
}

.menu-setting-btn:hover {
  color: #fff;
  transform: translateY(-50%) scale(1.1);
}

/* 拖拽相关样式 */
.draggable-item {
  cursor: default;
}

.is-drag-mode .draggable-item {
  cursor: move;
}

.drag-handle {
  position: absolute;
  right: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

.ghost {
  opacity: 0.5;
  background: rgba(0, 0, 0, 0.2);
}

/* 菜单项样式 - 使用deep覆盖Element UI默认样式 */
:deep(.el-menu-item) {
  background-color: transparent !important;
  color: #fff !important;
}

:deep(.el-menu-item:hover) {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

:deep(.el-menu-item.is-active) {
  background-color: rgba(0, 0, 0, 0.15) !important;
}

:deep(.el-submenu__title) {
  background-color: transparent !important;
  color: #fff !important;
}

:deep(.el-submenu__title:hover) {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.el-menu-item {
  height: 50px;
  line-height: 50px;
  margin: 4px 0;
  border-radius: 0;
}

.el-menu-item i {
  margin-right: 8px;
  font-size: 16px;
}

/* 右侧内容区 */
.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 顶部导航 */
.top-header {
  height: 64px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.collapse-btn {
  font-size: 20px;
  color: #303133;
  padding: 8px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.header-item {
  position: relative;
  cursor: pointer;
  font-size: 18px;
  color: #606266;
  transition: color 0.3s;
}

.header-item:hover {
  color: #FF8C00;
}

.badge {
  position: absolute;
  top: -2px;
  right: -2px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background 0.3s;
}

.user-info:hover {
  background: #f5f7fa;
}

.username {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

/* 物业账号绑定小区显示 - 使用 CSS 变量 */
.community-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

/* 城市运营商绑定城市显示 - 使用 CSS 变量 */
.city-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background: linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(255, 140, 0, 0.3);
}

.city-badge i {
  font-size: 14px;
}

.city-name {
  font-size: 13px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.community-badge i {
  font-size: 14px;
}

.community-name {
  font-size: 13px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 主内容区 */
.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

/* 菜单排序对话框样式 */
.menu-order-content {
  padding: 10px 0;
}

.tip-text {
  color: #909399;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
}

.menu-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f5f7fa;
  margin: 8px 0;
  border-radius: 4px;
  transition: all 0.3s;
}

.menu-item-row:hover {
  background: #e6f7ff;
}

.menu-item-row .el-checkbox {
  display: flex;
  align-items: center;
}

.menu-item-row .el-checkbox i {
  margin-right: 8px;
  font-size: 16px;
}

.order-buttons {
  display: flex;
  gap: 4px;
}

.order-buttons .el-button {
  padding: 4px 6px;
  color: #fff;
}

.order-buttons .el-button:disabled {
  background: #ccc;
  border-color: #ccc;
}

/* 固定标识 */
.fixed-label {
  color: #999;
  font-size: 12px;
  padding: 2px 8px;
  background: #f5f5f5;
  border-radius: 10px;
}

/* 设置齿轮按钮 */
.setting-btn {
  margin-left: auto;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s;
  padding: 5px;
}

.setting-btn:hover {
  opacity: 1;
}

/* 响应式 */
@media (max-width: 768px) {
  .sidebar {
    width: 64px;
  }
  
  .logo-text {
    display: none;
  }
  
  .sidebar-menu span {
    display: none;
  }
}
</style>
